import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { usePageTitle } from '../../hooks/usePageTitle'
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Eye,
  X,
  Calendar,
  Loader2
} from 'lucide-react'
import { pointsApi } from '../../lib/api'

const UserVerify = () => {
  usePageTitle()
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null)
  const [records, setRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    window.scrollTo(0, 0)
    loadRecords()
  }, [])

  const loadRecords = () => {
    setLoading(true)
    pointsApi.records({ status: 'pending', page_size: 100 })
      .then((res: any) => {
        setRecords(res.records || [])
      })
      .catch(() => setRecords([]))
      .finally(() => setLoading(false))
  }

  const handleConfirm = (record: any) => {
    pointsApi.confirm(record.id)
      .then(() => loadRecords())
      .catch(() => alert('审核失败'))
  }

  const pendingRecords = records

  return (
    <div className="bg-gray-50 min-h-screen pb-6">
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        </div>
      )}
      {/* 顶部导航 */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 py-4 flex items-center space-x-4 sticky top-0 z-10">
        <Link to="/user/profile" className="p-2 hover:bg-white/20 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-white" />
        </Link>
        <h1 className="text-lg font-bold text-white">待审核</h1>
        {pendingRecords.length > 0 && (
          <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{pendingRecords.length}</span>
        )}
      </div>

      {/* 列表 */}
      <div className="px-4 pt-4 space-y-3">
        {pendingRecords.length === 0 ? (
          <div className="bg-white rounded-xl p-10 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">暂无待审核记录</p>
            <p className="text-gray-400 text-sm mt-1">所有记录已处理完毕</p>
          </div>
        ) : pendingRecords.map(record => (
          <div key={record.id} className="bg-white rounded-xl shadow-sm p-4">
            <div class className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-11 h-11 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-orange-700 font-bold text-sm">{(record.user_name || '').charAt(0)}</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{record.user_name || ''}</p>
                  <p className="text-xs text-gray-400">{record.user_phone || ''}</p>
                </div>
              </div>
              <div className="flex items-center space-x-1 bg-orange-50 px-3 py-1 rounded-full">
                <Clock className="w-3 h-3 text-orange-600" />
                <span className="text-xs font-medium text-orange-700">待审核</span>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center text-gray-500">
                <Calendar className="w-4 h-4 mr-1" />
                <span>{record.event_type_name || ''}</span>
              </div>
              <span className="font-bold text-green-600">+{record.points || 0}分</span>
            </div>

            <div className="flex items-center justify-end mt-3 pt-3 border-t border-gray-50 space-x-2">
              <button
                onClick={() => setSelectedRecord(record)}
                className="flex items-center space-x-1 px-3 py-1.5 text-gray-500 hover:bg-gray-100 rounded-lg text-sm"
              >
                <Eye className="w-4 h-4" />
                <span>查看详情</span>
              </button>
              <button
                onClick={() => handleConfirm(record)}
                className="flex items-center space-x-1 px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4" />
                <span>审核通过</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 详情弹窗 */}
      {selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
          <div className="bg-white rounded-t-3xl w-full max-w-md px-6 py-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">审核详情</h3>
              <button onClick={() => setSelectedRecord(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="space-y-2 text-sm">
              {[
                ['用户姓名', selectedRecord.user_name || ''],
                ['手机号', selectedRecord.user_phone || ''],
                ['事件类型', selectedRecord.event_type_name || ''],
                ['获得积分', `+${selectedRecord.points || 0}`],
                ['机构', selectedRecord.hospital_name || selectedRecord.hospital || ''],
                ['操作员', selectedRecord.operator_name || selectedRecord.operatorName || ''],
                ['创建时间', selectedRecord.created_at || selectedRecord.createdAt || ''],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-medium text-gray-800">{value}</span>
                </div>
              ))}
            </div>
            <div className="flex space-x-3 mt-5">
              <button onClick={() => setSelectedRecord(null)} className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50">取消</button>
              <button
                onClick={() => {
                  handleConfirm(selectedRecord)
                  setSelectedRecord(null)
                }}
                className="flex-1 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-medium"
              >
                审核通过
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserVerify